import { useEffect, useState } from 'react';
import { aiWorkforceAPI, tasksAPI } from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger
} from '../components/ui/collapsible';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import {
  Brain, Cpu, Sparkles, Bot, CheckCircle, Clock, AlertCircle, XCircle,
  ChevronDown, ChevronRight, Eye, ThumbsUp, ThumbsDown, RefreshCw,
  Search, Filter, BarChart3, Zap, Users, MessageSquare
} from 'lucide-react';

// Brain type icons and colors
const BRAIN_CONFIG = {
  left: {
    label: 'Left Brain',
    sublabel: 'GPT-4o - Logical/Analytical',
    icon: Cpu,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200'
  },
  right: {
    label: 'Right Brain',
    sublabel: 'Claude Sonnet 4.5 - Creative/Intuitive',
    icon: Sparkles,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200'
  },
  cortex: {
    label: 'Cortex',
    sublabel: 'Gemini 3 Pro - Synthesis',
    icon: Brain,
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200'
  }
};

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-slate-100 text-slate-700', icon: Clock },
  processing: { label: 'Processing', color: 'bg-blue-100 text-blue-700', icon: RefreshCw },
  awaiting_approval: { label: 'Awaiting Approval', color: 'bg-amber-100 text-amber-700', icon: AlertCircle },
  approved: { label: 'Approved', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700', icon: XCircle },
  completed: { label: 'Completed', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  failed: { label: 'Failed', color: 'bg-red-100 text-red-700', icon: XCircle }
};

export default function AIWorkspacePage() {
  const [workItems, setWorkItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [reasoning, setReasoning] = useState(null);
  const [detailDialog, setDetailDialog] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [agentFilter, setAgentFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [agents, setAgents] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, awaiting_approval: 0, completed: 0 });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [workRes, agentsRes] = await Promise.all([
        aiWorkforceAPI.getWorkItems(),
        aiWorkforceAPI.getAgents()
      ]);
      
      const items = workRes.data || [];
      setWorkItems(items);
      setAgents(agentsRes.data || []);
      
      // Calculate stats
      setStats({
        total: items.length,
        pending: items.filter(i => i.status === 'pending').length,
        awaiting_approval: items.filter(i => i.status === 'awaiting_approval').length,
        completed: items.filter(i => ['approved', 'completed'].includes(i.status)).length
      });
    } catch (error) {
      console.error('Failed to fetch AI workspace data:', error);
      toast.error('Failed to load AI workspace');
    } finally {
      setLoading(false);
    }
  };

  const openItemDetail = async (item) => {
    setSelectedItem(item);
    setDetailDialog(true);
    
    if (item.reasoning_id) {
      try {
        const res = await aiWorkforceAPI.getReasoning(item.reasoning_id);
        setReasoning(res.data);
      } catch (error) {
        console.error('Failed to fetch reasoning:', error);
        setReasoning(null);
      }
    }
  };

  const handleApproval = async (approved) => {
    if (!selectedItem) return;
    
    try {
      await aiWorkforceAPI.approveExecution(selectedItem.execution_id, {
        approved,
        feedback: approved ? 'Approved via AI Workspace' : null,
        rejection_reason: !approved ? 'Rejected via AI Workspace' : null
      });
      
      toast.success(approved ? 'Task approved' : 'Task rejected');
      setDetailDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to update approval status');
    }
  };

  const filteredItems = workItems.filter(item => {
    const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
    const matchesAgent = agentFilter === 'all' || item.lead_agent_type === agentFilter;
    const matchesSearch = !searchQuery || 
      item.task_title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.lead_agent_name?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesAgent && matchesSearch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in space-y-6" data-testid="ai-workspace-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Brain className="h-7 w-7 text-violet-600" />
            AI Workspace
          </h1>
          <p className="text-muted-foreground">Monitor AI task processing, view reasoning, and manage approvals</p>
        </div>
        <Button onClick={fetchData} variant="outline">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Tasks</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
              <BarChart3 className="h-8 w-8 text-slate-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pending</p>
                <p className="text-2xl font-bold text-slate-600">{stats.pending}</p>
              </div>
              <Clock className="h-8 w-8 text-slate-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-amber-200 bg-amber-50/30">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Awaiting Approval</p>
                <p className="text-2xl font-bold text-amber-600">{stats.awaiting_approval}</p>
              </div>
              <AlertCircle className="h-8 w-8 text-amber-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-emerald-200 bg-emerald-50/30">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Completed</p>
                <p className="text-2xl font-bold text-emerald-600">{stats.completed}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-emerald-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4">
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search tasks or agents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="processing">Processing</SelectItem>
                <SelectItem value="awaiting_approval">Awaiting Approval</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={agentFilter} onValueChange={setAgentFilter}>
              <SelectTrigger className="w-[200px]">
                <Bot className="h-4 w-4 mr-2" />
                <SelectValue placeholder="All Agents" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Agents</SelectItem>
                {agents.map(agent => (
                  <SelectItem key={agent.id} value={agent.id}>
                    {agent.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Work Items List */}
      <Card>
        <CardHeader>
          <CardTitle>AI Task Executions</CardTitle>
          <CardDescription>
            {filteredItems.length} task{filteredItems.length !== 1 ? 's' : ''} found
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[500px]">
            <div className="space-y-3">
              {filteredItems.map(item => {
                const statusConfig = STATUS_CONFIG[item.status] || STATUS_CONFIG.pending;
                const StatusIcon = statusConfig.icon;
                
                return (
                  <Card 
                    key={item.id} 
                    className="cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => openItemDetail(item)}
                    data-testid={`ai-work-item-${item.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-medium truncate">{item.task_title}</h4>
                            <Badge className={statusConfig.color}>
                              <StatusIcon className="h-3 w-3 mr-1" />
                              {statusConfig.label}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-3 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Bot className="h-4 w-4" />
                              {item.lead_agent_name}
                            </span>
                            {item.collaborating_agents?.length > 0 && (
                              <span className="flex items-center gap-1">
                                <Users className="h-4 w-4" />
                                +{item.collaborating_agents.length} collaborators
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <Clock className="h-4 w-4" />
                              {formatDistanceToNow(new Date(item.created_at), { addSuffix: true })}
                            </span>
                          </div>
                          {item.summary && (
                            <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                              {item.summary}
                            </p>
                          )}
                        </div>
                        <Button variant="ghost" size="sm">
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
              
              {filteredItems.length === 0 && (
                <div className="text-center py-12">
                  <Brain className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                  <p className="text-muted-foreground">No AI task executions found</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Assign tasks to AI agents to see them here
                  </p>
                </div>
              )}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Detail Dialog with Reasoning */}
      <Dialog open={detailDialog} onOpenChange={setDetailDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Brain className="h-5 w-5 text-violet-600" />
              AI Reasoning & Output
            </DialogTitle>
            <DialogDescription>
              {selectedItem?.task_title}
            </DialogDescription>
          </DialogHeader>
          
          {selectedItem && (
            <div className="space-y-6">
              {/* Status & Agent Info */}
              <div className="flex items-center gap-4">
                <Badge className={STATUS_CONFIG[selectedItem.status]?.color || 'bg-slate-100'}>
                  {STATUS_CONFIG[selectedItem.status]?.label || selectedItem.status}
                </Badge>
                <span className="text-sm text-muted-foreground flex items-center gap-1">
                  <Bot className="h-4 w-4" />
                  {selectedItem.lead_agent_name}
                </span>
                {selectedItem.started_at && (
                  <span className="text-sm text-muted-foreground">
                    Started: {format(new Date(selectedItem.started_at), 'PPp')}
                  </span>
                )}
              </div>
              
              <Separator />
              
              {/* Reasoning Breakdown */}
              {reasoning ? (
                <Tabs defaultValue="cortex" className="w-full">
                  <TabsList className="grid w-full grid-cols-4">
                    <TabsTrigger value="left" className="flex items-center gap-1">
                      <Cpu className="h-4 w-4" />
                      Left Brain
                    </TabsTrigger>
                    <TabsTrigger value="right" className="flex items-center gap-1">
                      <Sparkles className="h-4 w-4" />
                      Right Brain
                    </TabsTrigger>
                    <TabsTrigger value="cortex" className="flex items-center gap-1">
                      <Brain className="h-4 w-4" />
                      Cortex
                    </TabsTrigger>
                    <TabsTrigger value="summary" className="flex items-center gap-1">
                      <Zap className="h-4 w-4" />
                      Summary
                    </TabsTrigger>
                  </TabsList>
                  
                  {/* Left Brain Tab */}
                  <TabsContent value="left" className="mt-4">
                    <BrainAssessmentCard 
                      brain={reasoning.left_brain} 
                      config={BRAIN_CONFIG.left}
                    />
                  </TabsContent>
                  
                  {/* Right Brain Tab */}
                  <TabsContent value="right" className="mt-4">
                    <BrainAssessmentCard 
                      brain={reasoning.right_brain} 
                      config={BRAIN_CONFIG.right}
                    />
                  </TabsContent>
                  
                  {/* Cortex Tab */}
                  <TabsContent value="cortex" className="mt-4">
                    <BrainAssessmentCard 
                      brain={reasoning.cortex} 
                      config={BRAIN_CONFIG.cortex}
                    />
                  </TabsContent>
                  
                  {/* Summary Tab */}
                  <TabsContent value="summary" className="mt-4">
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Final Output</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="bg-slate-50 p-4 rounded-lg">
                          <p className="whitespace-pre-wrap">{reasoning.final_output}</p>
                        </div>
                        
                        {reasoning.final_recommendations?.length > 0 && (
                          <div>
                            <h4 className="font-medium mb-2">Key Recommendations</h4>
                            <ul className="space-y-1">
                              {reasoning.final_recommendations.map((rec, idx) => (
                                <li key={idx} className="flex items-start gap-2 text-sm">
                                  <CheckCircle className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
                                  {rec}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div className="p-3 bg-slate-50 rounded-lg">
                            <p className="text-muted-foreground">Processing Time</p>
                            <p className="font-medium">{reasoning.total_processing_time_ms}ms</p>
                          </div>
                          <div className="p-3 bg-slate-50 rounded-lg">
                            <p className="text-muted-foreground">Tokens Used</p>
                            <p className="font-medium">{reasoning.total_tokens_used}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>
                </Tabs>
              ) : (
                <div className="text-center py-8">
                  <Brain className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                  <p className="text-muted-foreground">Loading reasoning data...</p>
                </div>
              )}
              
              {/* Approval Actions */}
              {selectedItem.status === 'awaiting_approval' && (
                <div className="flex items-center justify-end gap-3 pt-4 border-t">
                  <Button 
                    variant="outline" 
                    onClick={() => handleApproval(false)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <ThumbsDown className="h-4 w-4 mr-2" />
                    Reject
                  </Button>
                  <Button 
                    onClick={() => handleApproval(true)}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    <ThumbsUp className="h-4 w-4 mr-2" />
                    Approve
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Brain Assessment Card Component
function BrainAssessmentCard({ brain, config }) {
  const [expanded, setExpanded] = useState(true);
  
  if (!brain) {
    return (
      <Card className={`${config.bgColor} ${config.borderColor} border`}>
        <CardContent className="py-8 text-center">
          <config.icon className={`h-8 w-8 mx-auto ${config.color} mb-2`} />
          <p className="text-muted-foreground">No assessment available</p>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card className={`${config.bgColor} ${config.borderColor} border`}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <config.icon className={`h-5 w-5 ${config.color}`} />
            <div>
              <CardTitle className="text-lg">{config.label}</CardTitle>
              <CardDescription>{config.sublabel}</CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Badge variant="outline">
              {brain.confidence_score?.toFixed(0)}% confidence
            </Badge>
            <Badge variant="outline">
              {brain.processing_time_ms}ms
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Collapsible open={expanded} onOpenChange={setExpanded}>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="mb-2">
              {expanded ? <ChevronDown className="h-4 w-4 mr-1" /> : <ChevronRight className="h-4 w-4 mr-1" />}
              {expanded ? 'Collapse' : 'Expand'} Assessment
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="bg-white/80 p-4 rounded-lg">
              <p className="whitespace-pre-wrap text-sm">{brain.assessment}</p>
            </div>
            
            {brain.key_points?.length > 0 && (
              <div className="mt-4">
                <h4 className="font-medium text-sm mb-2">Key Points</h4>
                <ul className="space-y-1">
                  {brain.key_points.map((point, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      <span className={`${config.color} font-bold`}>•</span>
                      {point}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CollapsibleContent>
        </Collapsible>
      </CardContent>
    </Card>
  );
}
